import * as React from 'react'
import type { VariantProps } from 'class-variance-authority'

import { Button, buttonVariants } from '@/components/ui/button'

/**
 * Aurora IconButton — accessible wrapper around `<Button size="icon">`.
 *
 * The shared Button supports a `size="icon"` variant, but every consumer
 * still has to remember to:
 *  - pass an `aria-label`,
 *  - render an `<span class="sr-only">` for screen readers when the visible
 *    glyph is the only content,
 *  - keep the touch target above 40px on mobile.
 *
 * IconButton enforces all three. `label` is required (TypeScript will yell
 * if you forget) and it becomes both the `aria-label` and the visually
 * hidden screen-reader text. Sizes are limited to the three icon sizes that
 * Button supports.
 *
 * ```tsx
 * <IconButton label="Open menu" variant="ghost">
 *   <Menu />
 * </IconButton>
 * ```
 */
type IconButtonVariant = NonNullable<VariantProps<typeof buttonVariants>['variant']>
type IconButtonSize = Extract<
  NonNullable<VariantProps<typeof buttonVariants>['size']>,
  'icon' | 'icon-sm' | 'icon-lg'
>

interface IconButtonProps
  extends Omit<React.ComponentProps<'button'>, 'children' | 'aria-label'> {
  /**
   * Visible-to-screen-readers description of the action. Becomes both
   * `aria-label` and an `<span class="sr-only">` so a hover tooltip is
   * still discoverable to assistive tech.
   */
  label: string
  variant?: IconButtonVariant
  size?: IconButtonSize
  /** The icon to render — should be a Lucide icon element. */
  children: React.ReactNode
}

function IconButton({
  label,
  size = 'icon',
  variant = 'ghost',
  children,
  ...props
}: IconButtonProps) {
  return (
    <Button
      aria-label={label}
      title={label}
      size={size}
      variant={variant}
      data-slot="icon-button"
      {...props}
    >
      {children}
      <span className="sr-only">{label}</span>
    </Button>
  )
}

export { IconButton }
export type { IconButtonProps }
